import React, { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Fixed background: 3 radial glow blobs + floating particles + cursor spotlight.
 * Glow opacity rises with scroll. Hue shifts via CSS var --glow-mode set by sections.
 */
export const BackgroundLayer = ({ scrollProgress = 0 }) => {
  const reduce = useReducedMotion();
  const spotlightRef = useRef(null);
  const targetRef = useRef({ x: -9999, y: -9999 });
  const currentRef = useRef({ x: -9999, y: -9999 });
  const rafRef = useRef(null);
  const isTouchRef = useRef(false);

  useEffect(() => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    isTouchRef.current = isTouch;
    if (isTouch || reduce) return;

    const onMove = (e) => {
      targetRef.current.x = e.clientX;
      targetRef.current.y = e.clientY;
    };

    const tick = () => {
      const t = targetRef.current;
      const c = currentRef.current;
      // Smooth follow (lerp). Higher = snappier, lower = smoother.
      // 0.18 keeps a buttery feel without lag.
      c.x += (t.x - c.x) * 0.18;
      c.y += (t.y - c.y) * 0.18;
      const el = spotlightRef.current;
      if (el) {
        el.style.transform = `translate3d(${c.x - 300}px, ${c.y - 300}px, 0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [reduce]);

  // Opacity ramps from 0 (top) → 0.75 (after hero), capped on mobile in CSS layer
  const baseOpacity = Math.min(0.75, scrollProgress * 1.3);
  const isTouch = typeof window !== "undefined" &&
    window.matchMedia?.("(pointer: coarse)").matches;

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden>
      {/* Glow blobs */}
      <div
        className="absolute inset-0"
        style={{
          opacity: baseOpacity,
          transition: "opacity 300ms var(--ease-premium)",
          mixBlendMode: "screen",
        }}
      >
        <motion.div
          className="absolute"
          style={{
            bottom: "-20vh",
            left: "15vw",
            width: "70vw",
            height: "70vw",
            background: "radial-gradient(circle, var(--amber) 0%, transparent 60%)",
            filter: "blur(120px)",
          }}
          animate={reduce ? {} : { x: [0, 18, -12, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute"
          style={{
            top: "-10vh",
            right: "-10vw",
            width: "60vw",
            height: "60vw",
            background: "radial-gradient(circle, var(--magenta) 0%, transparent 60%)",
            filter: "blur(140px)",
            opacity: 0.85,
          }}
          animate={reduce ? {} : { x: [0, -16, 12, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute"
          style={{
            top: "30vh",
            left: "-15vw",
            width: "45vw",
            height: "45vw",
            background: "radial-gradient(circle, var(--flame) 0%, transparent 60%)",
            filter: "blur(100px)",
            opacity: 0.85,
          }}
          animate={reduce ? {} : { x: [0, 20, 0, -10, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Floating particles */}
      {!reduce && <Particles count={isTouch ? 8 : 16} />}

      {/* Cursor spotlight — DOM-driven (RAF + translate3d) for buttery 60fps */}
      {!isTouch && !reduce && (
        <div
          ref={spotlightRef}
          className="absolute will-change-transform"
          style={{
            left: 0,
            top: 0,
            width: 600,
            height: 600,
            background:
              "radial-gradient(circle, rgba(244,152,0,0.25) 0%, transparent 60%)",
            mixBlendMode: "screen",
            transform: "translate3d(-9999px, -9999px, 0)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Subtle grain */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.4 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          mixBlendMode: "overlay",
        }}
      />
    </div>
  );
};

const Particles = ({ count }) => {
  const seed = useRef(
    Array.from({ length: count }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 20,
      duration: 22 + Math.random() * 18,
      size: 1 + Math.random() * 1.4,
      hue: Math.random() < 0.1 ? "var(--magenta)" : "var(--amber)",
      key: i,
    }))
  );
  return (
    <div className="absolute inset-0">
      {seed.current.map((p) => (
        <motion.span
          key={p.key}
          className="absolute rounded-full"
          style={{
            left: `${p.left}%`,
            bottom: -10,
            width: p.size,
            height: p.size,
            background: p.hue,
            boxShadow: `0 0 ${p.size * 6}px ${p.hue}`,
          }}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: "-110vh", opacity: [0, 0.6, 0] }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
    </div>
  );
};
